import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["toggle", "menu", "themeToggle"]

  connect() {
    this.mediaQuery = window.matchMedia("(min-width: 960px)")
    this.onResize = () => this.setOpen(false)
    this.mediaQuery.addEventListener("change", this.onResize)
    this.setOpen(false)
    this.lastScrollY = Math.max(0, window.scrollY)
    this.onScroll = () => {
      if (this.scrollFrame) return
      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null
        this.updateScrollVisibility()
      })
    }
    this.onFocus = () => this.showHeader()
    window.addEventListener("scroll", this.onScroll, { passive: true })
    this.element.addEventListener("focusin", this.onFocus)
    this.showHeader()
    this.syncThemeToggle()
  }

  disconnect() {
    this.setOpen(false)
    this.mediaQuery.removeEventListener("change", this.onResize)
    window.removeEventListener("scroll", this.onScroll)
    this.element.removeEventListener("focusin", this.onFocus)
    if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame)
    this.scrollFrame = null
    this.element.classList.remove("lp-header-hidden")
  }

  showHeader() {
    this.element.classList.remove("lp-header-hidden")
    this.lastScrollY = Math.max(0, window.scrollY)
  }

  updateScrollVisibility() {
    const currentY = Math.max(0, window.scrollY)
    const menuOpen = this.toggleTarget.getAttribute("aria-expanded") === "true"
    if (currentY <= this.element.offsetHeight || menuOpen || this.element.querySelector(":focus-visible")) {
      this.showHeader()
      return
    }

    const delta = currentY - this.lastScrollY
    // Ignore tiny movements so trackpads do not make the header flicker.
    if (Math.abs(delta) < 8) return
    this.element.classList.toggle("lp-header-hidden", delta > 0)
    this.lastScrollY = currentY
  }

  toggle() {
    this.setOpen(this.toggleTarget.getAttribute("aria-expanded") !== "true")
  }

  close(event) {
    if (event && event.key !== "Escape") return
    if (this.toggleTarget.getAttribute("aria-expanded") !== "true") return
    this.setOpen(false)
    this.toggleTarget.focus()
  }

  navigate(event) {
    if (event.target.closest("a")) this.setOpen(false)
  }

  setOpen(open) {
    if (open) this.showHeader()
    this.toggleTarget.setAttribute("aria-expanded", String(open))
    this.menuTarget.classList.toggle("is-open", open)
    this.element.classList.toggle("is-menu-open", open)
    document.documentElement.classList.toggle("lp-menu-open", open)
    this.toggleTarget.setAttribute("aria-label", open ? this.toggleTarget.dataset.closeLabel : this.toggleTarget.dataset.openLabel)
    if (open) {
      this.backgroundElements = [...document.body.children].filter(element => element !== this.element && !element.inert)
      this.backgroundElements.forEach(element => { element.inert = true })
    } else {
      ;(this.backgroundElements || []).forEach(element => { element.inert = false })
      this.backgroundElements = []
    }
  }

  trapFocus(event) {
    if (event.key !== "Tab" || this.toggleTarget.getAttribute("aria-expanded") !== "true") return
    const elements = [...this.element.querySelectorAll('a[href], button:not([disabled])')].filter(element => element.getClientRects().length)
    const first = elements[0]
    const last = elements[elements.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  toggleTheme() {
    const next = document.documentElement.getAttribute("data-lp-theme") === "dark" ? "light" : "dark"
    document.documentElement.setAttribute("data-lp-theme", next)
    try {
      window.localStorage.setItem("lp-theme", next)
    } catch (e) {
      // Storage unavailable (private browsing, disabled cookies); theme still applies for this page view.
    }
    this.syncThemeToggle()
  }

  syncThemeToggle() {
    if (!this.hasThemeToggleTarget) return
    const isDark = document.documentElement.getAttribute("data-lp-theme") === "dark"
    this.themeToggleTarget.setAttribute("aria-pressed", String(isDark))
  }
}
